<template>
  <v-app>
    <v-navigation-drawer v-if="superuserLoggedIn" v-model="drawer" app>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="title"> cip </v-list-item-title>
          <v-list-item-subtitle>
            {{ $t('navbar.text.subName') }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider />

      <v-list dense nav>
        <v-list-item v-for="item in items" :key="item.title" link :to="item.to">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar v-if="loggedIn" app color="primary" dark>
      <v-flex class="d-flex align-center">
        <v-app-bar-nav-icon
          v-if="superuserLoggedIn"
          @click="drawer = !drawer"
        />
        <v-toolbar-title v-if="superuserLoggedIn">
          {{ $t('navbar.text.title') }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="mr-4">
          <v-menu v-if="loggedIn" offset-y>
            <template #activator="{ on }">
              <v-btn icon v-on="on" class="text-none">
                <v-icon>language</v-icon>
                <span>{{ $t(`language.${$i18n.locale}`) }}</span>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                :input-value="$i18n.locale === 'ja'"
                @click="onChangeLanguage('ja')"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('language.ja') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                :input-value="$i18n.locale === 'en'"
                @click="onChangeLanguage('en')"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('language.en') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                :input-value="$i18n.locale === 'th'"
                @click="onChangeLanguage('th')"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('language.th') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar-items>
        <v-toolbar-items v-if="superuserLoggedIn">
          <v-menu v-if="loggedIn" offset-y>
            <template #activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item :href="shiftURL" target="_blank">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('menu.text.shiftTable') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :href="shiftPlanURL" target="_blank">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('menu.text.shiftPlan') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :href="djangoAdminURL" target="_blank">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('menu.text.adminSite') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
              <v-list-item @click="logout">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('menu.button.logout') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar-items>
      </v-flex>
    </v-app-bar>
    <v-main>
      <!-- Keyed on the locale so the whole view subtree remounts on a
           language change. This re-runs data()/created() and refetches with
           the new `locale` header, which matters because part of the copy is
           translated by the BE. -->
      <router-view :key="$i18n.locale" />
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { switchLocale } from './router'
export default {
  name: 'App',
  metaInfo() {
    return {
      title: String(this.$t('navbar.text.appTitle')),
    }
  },
  data: () => ({
    drawer: true,
  }),
  computed: {
    ...mapGetters(['loggedIn', 'superuserLoggedIn']),
    langPrefix() {
      return `/${this.$i18n.locale}`
    },
    items() {
      return [
        {
          icon: 'mdi-chart-timeline',
          title: this.$t('navigation.text.timeline'),
          to: {
            name: 'timeline-shop-selection',
            params: { lang: this.$i18n.locale },
            query: { autoDetectShop: true },
          },
        },
        {
          icon: 'mdi-face-agent',
          title: this.$t('navigation.text.customerManagement'),
          to: `${this.langPrefix}/customer`,
        },
        {
          icon: 'mdi-account',
          title: this.$t('navigation.text.appUserManagement'),
          to: `${this.langPrefix}/user`,
        },
        {
          icon: 'mdi-clock-outline',
          title: this.$t('navigation.text.todayNextReservation'),
          to: {
            name: 'reservation-today',
            params: { lang: this.$i18n.locale },
            query: { autoDetectShop: true },
          },
        },
        {
          icon: 'mdi-book-open-variant',
          title: this.$t('navigation.text.reservationList'),
          to: {
            name: 'reservation-list',
            params: { lang: this.$i18n.locale },
          },
        },
        {
          icon: 'mdi-history',
          title: this.$t('navigation.text.reservationChangeHistory'),
          to: `${this.langPrefix}/logs`,
        },
        {
          icon: 'mdi-file-delimited',
          title: this.$t('navigation.text.nextReservationOutput'),
          to: `${this.langPrefix}/kpi`,
        },
        {
          icon: 'mdi-file-delimited',
          title: this.$t('navigation.text.nextReservationOutputV2'),
          to: `${this.langPrefix}/kpi2`,
        },
        {
          icon: 'mdi-credit-card',
          title: this.$t('navigation.text.subscription'),
          to: `${this.langPrefix}/subscription`,
        },
        {
          icon: 'mdi-calendar',
          title: this.$t('navigation.text.businessDaySetting'),
          to: `${this.langPrefix}/openday`,
        },
        {
          icon: 'mdi-calendar',
          title: this.$t('navigation.text.holidaySetting'),
          to: `${this.langPrefix}/holiday`,
        },
        {
          icon: 'mdi-sync',
          title: this.$t('navigation.text.hotpepperIntegration'),
          to: `${this.langPrefix}/send_waku`,
        },
        {
          icon: 'mdi-history',
          title: this.$t('navigation.text.goodsPurchaseHistory'),
          to: `${this.langPrefix}/goods/purchase`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.attendance'),
          to: `${this.langPrefix}/timecard`,
        },
        {
          icon: 'mdi-star',
          title: this.$t('navigation.text.overflow'),
          to: `${this.langPrefix}/overflow`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.shift'),
          to: `${this.langPrefix}/shift`,
        },
        {
          icon: 'mdi-palette',
          title: this.$t('navigation.text.previousColor'),
          to: {
            path: `${this.langPrefix}/previous_color`,
            query: { autoDetectShop: true },
          },
        },
        {
          icon: 'mdi-shop-edit',
          title: this.$t('navigation.text.ecUser'),
          to: `${this.langPrefix}/ec_user`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.additionalSlot'),
          to: `${this.langPrefix}/additional_waku`,
        },
        {
          icon: 'mdi-account-network',
          title: this.$t('navigation.text.mergeCustomer'),
          to: `${this.langPrefix}/merge_customer`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.ticketUsageHistory'),
          to: `${this.langPrefix}/used_ticket`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.ticketPurchaseHistory'),
          to: `${this.langPrefix}/purchased_ticket`,
        },
        {
          icon: '',
          title: this.$t('navigation.text.firebaseUser'),
          to: `${this.langPrefix}/firebase_user`,
        },
      ]
    },
    shiftURL() {
      return process.env.VUE_APP_UGLY_ADMIN_BASE_URL + 'shift'
    },
    shiftPlanURL() {
      return process.env.VUE_APP_UGLY_ADMIN_BASE_URL + 'shift-plan'
    },
    djangoAdminURL() {
      return process.env.VUE_APP_DJANGO_ADMIN_URL
    },
  },
  methods: {
    ...mapActions(['clear']),
    logout() {
      this.clear()
      this.$router.push('/login')
    },
    onChangeLanguage(language) {
      // Same routine as Login.vue - see switchLocale() in src/router.ts
      switchLocale(this, language)
    },
  },
}
</script>

<style></style>
